import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Typeahead } from 'react-bootstrap-typeahead';
import { AdminHubHeader } from '../components/admin-hub/admin-hub-header';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import type { SelectOption } from '@web/components/select';
import {
    SORT_DIRECTION,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TablePagination,
    TableRow,
} from '@web/components/table';
import { Button, Icon, LoadingCircle, Typography } from '@packages/ui/shared';
import colors from '@packages/core/styles/colors';
import { getLogger } from '@packages/core/logging';
import { accountsService } from '@web/services/singletons';
import { RegionZoneDistrictMapping, RsqmDsqmUser } from '@packages/contexts/accounts';
import { usePublicationsDispatch } from '@web/vin-sessions/components/stis-publications/context/publications.hooks';
import { publicationsActions } from '@web/vin-sessions/components/stis-publications/context/publications.state';

const logger = getLogger('admin');

type AssignableUserOption = {
    accountId: string;
    label: string;
    soaUsername: string | null;
    firstName: string | null;
    lastName: string | null;
};

const UNASSIGN_OPTION_ID = '__UNASSIGN__';

type SortKey = 'soaUsername' | 'firstName' | 'lastName';

const getShouldUseDropup = (values: {
    inputTop: number;
    inputBottom: number;
    viewportHeight: number;
    estimatedMenuHeight: number;
    margin?: number;
}): boolean => {
    const margin = values.margin ?? 16;
    const spaceBelow = Math.max(0, values.viewportHeight - values.inputBottom - margin);
    const spaceAbove = Math.max(0, values.inputTop - margin);

    if (spaceBelow >= values.estimatedMenuHeight) {
        return false;
    }

    return spaceAbove > spaceBelow;
};

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20] as const;

type FilterTypeaheadProps = {
    id: string;
    options: SelectOption[];
    value: string;
    placeholder: string;
    disabled?: boolean;
    onValueChange(nextValue: string): void;
};

const FilterTypeahead: FC<FilterTypeaheadProps> = ({ id, options, value, placeholder, disabled, onValueChange }) => {
    const classes = useStyles();
    const typeaheadRef = useRef<any>(null);
    const [inputValue, setInputValue] = useState('');
    const previousValueRef = useRef<string>(value);

    const selected = useMemo(() => {
        if (!value) {
            return [];
        }

        const match = options.find((option) => option.value === value);
        return match ? [match] : [];
    }, [options, value]);

    const handleSelectionChange = useCallback(
        (selection: SelectOption[]) => {
            const nextValue = selection[0]?.value ?? '';
            onValueChange(nextValue);
            setInputValue('');
        },
        [onValueChange]
    );

    const handleClear = useCallback(() => {
        if (disabled) {
            return;
        }

        onValueChange('');

        const instance = typeaheadRef.current;

        if (instance) {
            instance.clear?.();
            instance.toggleMenu?.();
            instance.inputNode?.focus?.();
        }
        setInputValue('');
    }, [disabled, onValueChange]);

    const handleToggle = useCallback(() => {
        if (disabled) {
            return;
        }

        const instance = typeaheadRef.current;

        if (instance) {
            instance.toggleMenu?.();
            instance.inputNode?.focus?.();
        }
    }, [disabled]);

    useEffect(() => {
        if (previousValueRef.current && !value) {
            setInputValue('');

            const instance = typeaheadRef.current;

            if (instance) {
                instance.clear?.();
            }
        }

        previousValueRef.current = value;
    }, [value]);

    const hasSelection = Boolean(value);
    const hasTypedValue = inputValue.length > 0;
    const hasValue = hasSelection || hasTypedValue;

    const iconAction = hasValue ? handleClear : handleToggle;
    const iconName = hasValue ? 'x-close' : 'chevron-down';

    const filterOptions = useCallback(
        (option: SelectOption) => {
            const normalized = inputValue.trim().toLowerCase();

            if (!normalized) {
                return true;
            }

            const label = (option.title || option.value || '').toLowerCase();
            return label.includes(normalized);
        },
        [inputValue]
    );

    return (
        <Typeahead<SelectOption>
            id={id}
            ref={typeaheadRef}
            className={classes.filterTypeahead}
            labelKey="title"
            selected={selected}
            options={options}
            placeholder={placeholder}
            disabled={disabled}
            filterBy={(option) => filterOptions(option)}
            onChange={handleSelectionChange}
            onInputChange={(text) => {
                setInputValue(text);
            }}
        >
            <div
                className={classNames(classes.filterTypeaheadIcon, {
                    [classes.filterTypeaheadIconDisabled]: disabled,
                })}
            >
                <Icon name={iconName} onPress={disabled ? undefined : iconAction} />
            </div>
        </Typeahead>
    );
};

const formatUserDisplayLabel = ({
    firstName,
    lastName,
    soaUsername,
    accountId,
}: {
    firstName?: string | null;
    lastName?: string | null;
    soaUsername?: string | null;
    accountId?: string | null;
}): string => {
    const trimmedFirst = firstName?.trim() ?? '';
    const trimmedLast = lastName?.trim() ?? '';
    const trimmedUsername = soaUsername?.trim() ?? '';

    let nameSection = '';

    if (trimmedFirst && trimmedLast) {
        nameSection = `${trimmedFirst}, ${trimmedLast}`;
    } else if (trimmedFirst) {
        nameSection = trimmedFirst;
    } else if (trimmedLast) {
        nameSection = trimmedLast;
    }

    if (trimmedUsername) {
        return nameSection ? `${nameSection} (${trimmedUsername})` : trimmedUsername;
    }

    if (nameSection) {
        return nameSection;
    }

    return accountId?.trim() ?? '';
};

const useStyles = createUseStyles({
    pageContainer: {
        padding: '32px',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    selectGroup: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 24,
        width: '100%',
    },
    selectWrapper: {
        minWidth: 220,
        maxWidth: 300,
        width: 'clamp(220px, 22vw, 300px)',
        flexGrow: 0,
        flexShrink: 1,
        flexBasis: 'clamp(220px, 22vw, 300px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    filterTypeahead: {
        position: 'relative',
        width: '100%',
        '& .rbt': {
            width: '100%',
        },
        '& .rbt-aux': {
            display: 'none',
        },
        '& .rbt-input-main': {
            height: 40,
            paddingLeft: 12,
            paddingRight: 36,
        },
        '& .rbt-input-hint': {
            height: 40,
            paddingLeft: 12,
            paddingRight: 36,
        },
        '& .rbt-menu': {
            zIndex: 2000,
            maxHeight: 320,
            overflowY: 'auto',
        },
        '& .rbt-menu > .dropdown-item': {
            paddingLeft: 12,
            paddingRight: 12,
        },
    },
    filterTypeaheadIcon: {
        position: 'absolute',
        top: 8,
        right: 8,
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.white,
        borderRadius: 12,
        cursor: 'pointer',
        zIndex: 2,
        color: colors.grayFive,
        '&:hover': {
            backgroundColor: colors.grayOne,
        },
        '&:focus-visible': {
            outline: `2px solid ${colors.blueOne}`,
            outlineOffset: 2,
        },
        '& svg': {
            pointerEvents: 'none',
        },
    },
    filterTypeaheadIconDisabled: {
        cursor: 'default',
        color: colors.grayThree,
        backgroundColor: 'transparent',
        pointerEvents: 'none',
    },
    helperText: {
        marginTop: 16,
    },
    tableSection: {
        marginTop: 40,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    statusMessage: {
        color: colors.redOne,
    },
    tableLoader: {
        display: 'flex',
        justifyContent: 'center',
        padding: 24,
    },
    borderedTable: {
        '& table': {
            borderCollapse: 'collapse',
        },
    },
    tableHeaderGroup: {
        justifyContent: 'center',
        textAlign: 'center',
        backgroundColor: colors.grayOne,
    },
    tableHeaderSub: {
        justifyContent: 'center',
        textAlign: 'center',
        backgroundColor: colors.grayOne,
    },
    tableHeaderSpanTwo: {
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
    },
    tableBodyRow: {
        '& td': {
            borderRight: `1px solid ${colors.grayThree}`,
            borderBottom: `1px solid ${colors.grayThree}`,
        },
        '& td:first-child': {
            borderLeft: `1px solid ${colors.grayThree}`,
        },
    },
    tableCellOuter: {
        textAlign: 'center',
        verticalAlign: 'middle',
        padding: 0,
    },
    tableBodyCell: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        width: '100%',
        height: '100%',
        padding: '8px 12px !important',
        lineHeight: 1.2,
    },
    dsqmCell: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        alignItems: 'stretch',
        textAlign: 'left',
        width: '100%',
    },
    editRow: {
        backgroundColor: colors.blueFour,
    },
    usernameSelectWrapper: {
        width: '100%',
        minWidth: 280,
        maxWidth: 500,
        '& .rbt': {
            width: '100%',
        },
        '& .rbt-input-main': {
            height: 40,
            paddingLeft: 12,
            paddingRight: 12,
        },
        '& .rbt-menu': {
            minWidth: 240,
            zIndex: 2000,
        },
        '& .dropdown-menu': {
            minWidth: 280,
            maxHeight: 300,
            padding: '8px 0',
            borderRadius: 3,
            border: `1px solid ${colors.grayThree}`,
            boxShadow: '0px 16px 12px -12px rgba(0, 0, 0, 0.16)',
            overflowY: 'auto !important',
            overflowX: 'hidden !important',
            width: 'var(--dsqm-username-menu-width, auto)',
            maxWidth: 'var(--dsqm-username-menu-width, auto)',
        },
        '& .dropdown-menu::-webkit-scrollbar': {
            width: 10,
        },
        '& .dropdown-menu::-webkit-scrollbar-track': {
            backgroundColor: '#ffffff',
        },
        '& .dropdown-menu::-webkit-scrollbar-thumb': {
            backgroundColor: colors.blueFour,
            borderRadius: 10,
            border: '2px solid #ffffff',
        },
        '& .dropdown-menu > .dropdown-item': {
            padding: '5px 10px',
            height: 40,
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            textAlign: 'left',
            '&:hover': {
                backgroundColor: colors.blueFour,
                color: colors.black,
            },
            '& label': {
                display: 'flex',
                alignItems: 'center',
            },
            '&:hover label': {
                color: colors.black,
            },
            '& input[type="checkbox"]': {
                marginRight: 10,
            },
        },
    },
    editActions: {
        display: 'flex',
        justifyContent: 'center',
        gap: 8,
    },
    skeletonRow: {
        '& td': {
            padding: '12px 8px',
        },
    },
    skeletonCell: {
        height: 20,
        backgroundColor: '#e0e0e0',
        borderRadius: 4,
        animation: '$shimmer 1.5s infinite linear',
        backgroundImage: 'linear-gradient(90deg, #e0e0e0 0%, #f0f0f0 50%, #e0e0e0 100%)',
        backgroundSize: '200% 100%',
    },
    '@keyframes shimmer': {
        '0%': {
            backgroundPosition: '200% 0',
        },
        '100%': {
            backgroundPosition: '-200% 0',
        },
    },
});

export const ManageDsqmsReassignment: FC = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    const publicationDispatch = usePublicationsDispatch();
    const [allAssignments, setAllAssignments] = useState<RegionZoneDistrictMapping[]>([]);
    const [selectedRegionId, setSelectedRegionId] = useState('');
    const [selectedZoneId, setSelectedZoneId] = useState('');
    const [selectedDistrictId, setSelectedDistrictId] = useState('');
    const [editingRowId, setEditingRowId] = useState('');
    const [selectedAccountId, setSelectedAccountId] = useState('');
    const [selectedUserAccountId, setSelectedUserAccountId] = useState('');
    const [assignableUsers, setAssignableUsers] = useState<AssignableUserOption[]>([]);
    const [availableAssignableUsers, setAvailableAssignableUsers] = useState<AssignableUserOption[]>([]);
    const [loadingAssignableUsers, setLoadingAssignableUsers] = useState(false);
    const [accountsSearchTerm, setAccountsSearchTerm] = useState('');
    const [selectedAssignableOption, setSelectedAssignableOption] = useState<AssignableUserOption | null>(null);
    const [usernameMenuWidth, setUsernameMenuWidth] = useState<number | null>(null);
    const [shouldUseDropup, setShouldUseDropup] = useState(false);
    const [loadingAssignments, setLoadingAssignments] = useState(false);
    const [savingAssignment, setSavingAssignment] = useState(false);
    const [refreshingData, setRefreshingData] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[1]);
    const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: SORT_DIRECTION } | null>(null);

    const regionManuallyClearedRef = useRef(false);
    const zoneManuallyClearedRef = useRef(false);
    const districtManuallyClearedRef = useRef(false);

    const resetEditingState = useCallback(() => {
        setEditingRowId('');
        setSelectedAccountId('');
        setSelectedAssignableOption(null);
        setAccountsSearchTerm('');
        setAssignableUsers([]);
        setActionError(null);
        setShouldUseDropup(false);
    }, []);

    const updateDropDirection = useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }

        if (!editingRowId) {
            setShouldUseDropup(false);
            return;
        }

        const root = document.getElementById(`dsqm-user-typeahead-${editingRowId}`);
        const input = (root?.querySelector('input') as HTMLInputElement | null) ?? null;

        const fallbackInput = input
            ? null
            : ((root?.querySelector('.rbt-input-main') as HTMLInputElement | null) ?? null);

        const target = input ?? fallbackInput;
        if (!target) {
            return;
        }

        const rect = target.getBoundingClientRect();
        const estimatedMenuHeight = 280;

        setShouldUseDropup(
            getShouldUseDropup({
                inputTop: rect.top,
                inputBottom: rect.bottom,
                viewportHeight: window.innerHeight,
                estimatedMenuHeight,
            })
        );
    }, [editingRowId]);

    useEffect(() => {
        if (!editingRowId) {
            return;
        }

        const rafId = window.requestAnimationFrame(() => {
            updateDropDirection();
        });

        const handle = () => updateDropDirection();

        window.addEventListener('resize', handle);
        window.addEventListener('scroll', handle, true);

        return () => {
            window.cancelAnimationFrame(rafId);
            window.removeEventListener('resize', handle);
            window.removeEventListener('scroll', handle, true);
        };
    }, [editingRowId, updateDropDirection]);

    const getRowId = useCallback((assignment: RegionZoneDistrictMapping) => {
        return `${assignment.districtId}-${assignment.accountRoleId ?? assignment.accountId ?? 'unassigned'}`;
    }, []);

    const loadAssignments = useCallback(
        async (options?: { suppressLoading?: boolean }) => {
            const shouldManageLoading = !options?.suppressLoading;

            if (shouldManageLoading) {
                setLoadingAssignments(true);
            }

            setLoadError(null);
            resetEditingState();

            try {
                const response = await accountsService.getRegionZoneDistrictMap();

                if (!response.success) {
                    if (response.aborted) {
                        return;
                    }

                    throw response.data;
                }

                const results = response.data.regionZoneDistrictMap ?? [];

                setAllAssignments(results);
                if (!options?.suppressLoading) {
                    setCurrentPage(0);
                }
            } catch (error: unknown) {
                logger.error('Failed to load region/zone/district map', { error });

                const apiError = error as { message?: string; globalErrors?: string[] };
                const message =
                    (Array.isArray(apiError?.globalErrors) && apiError.globalErrors[0]) ||
                    apiError?.message ||
                    t(
                        'administration:manageDsqms.errors.loadFailure',
                        'Unable to load DSQM assignments. Please try again later.'
                    );
                setLoadError(message);
            } finally {
                if (shouldManageLoading) {
                    setLoadingAssignments(false);
                }
            }
        },
        [resetEditingState, setCurrentPage, t]
    );

    useEffect(() => {
        void loadAssignments();
    }, [loadAssignments]);

    const buildAssignableOption = useCallback(
        (values: {
            accountId?: string | null;
            soaUsername?: string | null;
            firstName?: string | null;
            lastName?: string | null;
        }) => {
            if (!values.accountId) {
                return null;
            }

            const label =
                formatUserDisplayLabel({
                    accountId: values.accountId,
                    soaUsername: values.soaUsername,
                    firstName: values.firstName,
                    lastName: values.lastName,
                }) || values.accountId;

            return {
                accountId: values.accountId,
                soaUsername: values.soaUsername ?? null,
                firstName: values.firstName ?? null,
                lastName: values.lastName ?? null,
                label,
            };
        },
        []
    );

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        setLoadingAssignableUsers(true);

        (async () => {
            try {
                const response = await accountsService.getRsqmDsqmUsers({ signal: controller.signal });

                if (!response.success) {
                    throw response.data;
                }

                const users = response.data.rsqmDsqmUsers ?? [];

                const mapped = users
                    .map((user: RsqmDsqmUser) =>
                        buildAssignableOption({
                            accountId: user.accountId,
                            soaUsername: user.soaUsername,
                            firstName: user.firstName,
                            lastName: user.lastName,
                        })
                    )
                    .filter((option): option is AssignableUserOption => Boolean(option));

                if (isMounted) {
                    const sorted = mapped.sort((a, b) => a.label.localeCompare(b.label));
                    setAvailableAssignableUsers(sorted);
                    setAssignableUsers(sorted);
                }
            } catch (error) {
                if ((error as { name?: string })?.name === 'AbortError') {
                    return;
                }

                logger.error('Failed to load RSQM DSQM users', { error });

                setActionError(
                    (previous) =>
                        previous ||
                        t(
                            'administration:manageDsqms.errors.loadAccountsFailure',
                            'Unable to load accounts. Please try again.'
                        )
                );
            } finally {
                if (isMounted) {
                    setLoadingAssignableUsers(false);
                }
            }
        })();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [accountsService, buildAssignableOption, t]);

    const editingAssignment = useMemo(() => {
        if (!editingRowId) {
            return null;
        }

        return allAssignments.find((item) => getRowId(item) === editingRowId) ?? null;
    }, [allAssignments, editingRowId, getRowId]);

    const getPreferredOption = useCallback(() => {
        if (selectedAssignableOption) {
            return selectedAssignableOption;
        }

        if (!editingAssignment) {
            return null;
        }

        return buildAssignableOption({
            accountId: editingAssignment.accountId,
            soaUsername: editingAssignment.soaUsername,
            firstName: editingAssignment.firstName,
            lastName: editingAssignment.lastName,
        });
    }, [buildAssignableOption, editingAssignment, selectedAssignableOption]);

    useEffect(() => {
        if (!editingRowId) {
            setAssignableUsers(availableAssignableUsers);
            return;
        }

        const normalizedQuery = accountsSearchTerm.trim().toLowerCase();
        const preferredOption = getPreferredOption();
        const effectivePreferredOption = preferredOption?.accountId === UNASSIGN_OPTION_ID ? null : preferredOption;

        const filtered = availableAssignableUsers.filter((user) => {
            if (effectivePreferredOption && user.accountId === effectivePreferredOption.accountId) {
                return true;
            }

            if (!normalizedQuery) {
                return true;
            }

            return user.label.toLowerCase().includes(normalizedQuery);
        });

        if (
            effectivePreferredOption &&
            !filtered.some((option) => option.accountId === effectivePreferredOption.accountId)
        ) {
            setAssignableUsers([effectivePreferredOption, ...filtered]);
            return;
        }

        setAssignableUsers(filtered);
    }, [accountsSearchTerm, availableAssignableUsers, editingRowId, getPreferredOption]);

    useEffect(() => {
        if (!editingRowId) {
            setUsernameMenuWidth(null);
            return;
        }

        if (typeof document === 'undefined') {
            return;
        }

        const measurementNode = document.createElement('div');
        measurementNode.style.position = 'absolute';
        measurementNode.style.visibility = 'hidden';
        measurementNode.style.whiteSpace = 'nowrap';
        measurementNode.style.font = getComputedStyle(document.body).font;
        document.body.appendChild(measurementNode);

        let maxWidth = 0;
        assignableUsers.forEach((option) => {
            measurementNode.textContent = option.label;
            maxWidth = Math.max(maxWidth, Math.ceil(measurementNode.offsetWidth));
        });

        document.body.removeChild(measurementNode);

        if (!maxWidth) {
            setUsernameMenuWidth(null);
            return;
        }

        const MIN_MENU_WIDTH = 240;
        const EXTRA_BUFFER = 20;
        const computedWidth = maxWidth + EXTRA_BUFFER;

        setUsernameMenuWidth(Math.max(MIN_MENU_WIDTH, computedWidth));
    }, [assignableUsers, editingRowId]);

    const usernameMenuStyle = useMemo<React.CSSProperties | undefined>(() => {
        if (!usernameMenuWidth) {
            return undefined;
        }

        return {
            ['--dsqm-username-menu-width' as const]: `${usernameMenuWidth}px`,
        } as React.CSSProperties;
    }, [usernameMenuWidth]);

    const regionOptions = useMemo<SelectOption[]>(() => {
        const seen = new Set<string>();
        const entries: Array<[string, string]> = [];

        allAssignments.forEach((item) => {
            const code = (item.regionCode || item.regionId || '').trim();
            if (!code || seen.has(code)) {
                return;
            }

            seen.add(code);
            entries.push([code, code]);
        });

        return entries.sort((a, b) => a[1].localeCompare(b[1])).map(([value, title]) => ({ value, title }));
    }, [allAssignments]);

    useEffect(() => {
        if (selectedRegionId) {
            regionManuallyClearedRef.current = false;
            return;
        }

        if (regionManuallyClearedRef.current) {
            return;
        }

        if (regionOptions.length !== 1) {
            return;
        }

        const [single] = regionOptions;
        if (!single?.value) {
            return;
        }

        setSelectedRegionId(single.value);
        setCurrentPage(0);
        resetEditingState();
    }, [regionOptions, resetEditingState, selectedRegionId]);

    useEffect(() => {
        if (!selectedRegionId) {
            return;
        }

        if (regionOptions.some((option) => option.value === selectedRegionId)) {
            return;
        }

        setSelectedRegionId('');
        setCurrentPage(0);
        resetEditingState();
    }, [regionOptions, resetEditingState, selectedRegionId]);

    const zoneOptions = useMemo<SelectOption[]>(() => {
        const seen = new Set<string>();
        const entries: Array<[string, string]> = [];

        allAssignments.forEach((item) => {
            const regionCodeOrId = (item.regionCode || item.regionId || '').trim();
            if (selectedRegionId && regionCodeOrId !== selectedRegionId) {
                return;
            }

            const code = (item.zoneCode || item.zoneId || '').trim();
            if (!code || seen.has(code)) {
                return;
            }

            seen.add(code);
            entries.push([code, code]);
        });

        return entries.sort((a, b) => a[1].localeCompare(b[1])).map(([value, title]) => ({ value, title }));
    }, [allAssignments, selectedRegionId]);

    useEffect(() => {
        if (selectedZoneId) {
            zoneManuallyClearedRef.current = false;
            return;
        }

        if (zoneManuallyClearedRef.current) {
            return;
        }

        if (zoneOptions.length !== 1) {
            return;
        }

        const [single] = zoneOptions;
        if (!single?.value) {
            return;
        }

        setSelectedZoneId(single.value);
        setCurrentPage(0);
        resetEditingState();
    }, [resetEditingState, selectedZoneId, zoneOptions]);

    useEffect(() => {
        if (!selectedZoneId) {
            return;
        }

        if (zoneOptions.some((option) => option.value === selectedZoneId)) {
            return;
        }

        setSelectedZoneId('');
        setSelectedDistrictId('');
        setSelectedUserAccountId('');
        setCurrentPage(0);
        resetEditingState();
    }, [resetEditingState, selectedZoneId, zoneOptions]);

    const districtOptions = useMemo<SelectOption[]>(() => {
        const seen = new Set<string>();
        const entries: Array<[string, string]> = [];

        allAssignments.forEach((item) => {
            const regionCodeOrId = (item.regionCode || item.regionId || '').trim();
            if (selectedRegionId && regionCodeOrId !== selectedRegionId) {
                return;
            }

            const zoneCodeOrId = (item.zoneCode || item.zoneId || '').trim();
            if (selectedZoneId && zoneCodeOrId !== selectedZoneId) {
                return;
            }

            const code = (item.districtCode || item.districtId || '').trim();
            if (!code || seen.has(code)) {
                return;
            }

            seen.add(code);
            entries.push([code, code]);
        });

        return entries.sort((a, b) => a[1].localeCompare(b[1])).map(([value, title]) => ({ value, title }));
    }, [allAssignments, selectedRegionId, selectedZoneId]);

    useEffect(() => {
        if (selectedDistrictId) {
            districtManuallyClearedRef.current = false;
            return;
        }

        if (districtManuallyClearedRef.current) {
            return;
        }

        if (districtOptions.length !== 1) {
            return;
        }

        const [single] = districtOptions;
        if (!single?.value) {
            return;
        }

        setSelectedDistrictId(single.value);
        setCurrentPage(0);
        resetEditingState();
    }, [districtOptions, resetEditingState, selectedDistrictId]);

    useEffect(() => {
        if (!selectedDistrictId) {
            return;
        }

        if (districtOptions.some((option) => option.value === selectedDistrictId)) {
            return;
        }

        setSelectedDistrictId('');
        setSelectedUserAccountId('');
        setCurrentPage(0);
        resetEditingState();
    }, [districtOptions, resetEditingState, selectedDistrictId]);

    const selectedRegionLabel = useMemo(() => {
        if (!selectedRegionId) {
            return null;
        }

        const option = regionOptions.find((item) => item.value === selectedRegionId);
        return option?.title || selectedRegionId;
    }, [regionOptions, selectedRegionId]);

    const selectedZoneLabel = useMemo(() => {
        if (!selectedZoneId) {
            return null;
        }

        const option = zoneOptions.find((item) => item.value === selectedZoneId);
        return option?.title || selectedZoneId;
    }, [selectedZoneId, zoneOptions]);

    const selectedDistrictLabel = useMemo(() => {
        if (!selectedDistrictId) {
            return null;
        }

        const option = districtOptions.find((item) => item.value === selectedDistrictId);
        return option?.title || selectedDistrictId;
    }, [districtOptions, selectedDistrictId]);

    const selectedUserLabel = useMemo(() => {
        if (!selectedUserAccountId) {
            return null;
        }

        const entry = allAssignments.find((item) => item.accountId === selectedUserAccountId);
        if (!entry) {
            return null;
        }

        const label = formatUserDisplayLabel({
            accountId: entry.accountId,
            soaUsername: entry.soaUsername,
            firstName: entry.firstName,
            lastName: entry.lastName,
        });

        return label || null;
    }, [allAssignments, selectedUserAccountId]);

    const filteredAssignments = useMemo<RegionZoneDistrictMapping[]>(() => {
        const normalize = (value?: string | null) => value?.trim() ?? '';

        return allAssignments.filter((item) => {
            const regionCodeOrId = normalize(item.regionCode) || normalize(item.regionId);
            const zoneCodeOrId = normalize(item.zoneCode) || normalize(item.zoneId);
            const districtCodeOrId = normalize(item.districtCode) || normalize(item.districtId);

            if (selectedRegionId && regionCodeOrId !== selectedRegionId) {
                return false;
            }

            if (selectedZoneId && zoneCodeOrId !== selectedZoneId) {
                return false;
            }

            if (selectedDistrictId && districtCodeOrId !== selectedDistrictId) {
                return false;
            }

            if (selectedUserAccountId) {
                if (!item.accountId || item.accountId !== selectedUserAccountId) {
                    return false;
                }
            }

            return true;
        });
    }, [allAssignments, selectedDistrictId, selectedRegionId, selectedUserAccountId, selectedZoneId]);

    const sortedAssignments = useMemo<RegionZoneDistrictMapping[]>(() => {
        if (!sortConfig) {
            return filteredAssignments;
        }

        const { key, direction } = sortConfig;

        const getValue = (assignment: RegionZoneDistrictMapping) => {
            let rawValue: string | null | undefined = '';

            switch (key) {
                case 'soaUsername':
                    rawValue = assignment.soaUsername;
                    break;
                case 'firstName':
                    rawValue = assignment.firstName;
                    break;
                case 'lastName':
                    rawValue = assignment.lastName;
                    break;
                default:
                    rawValue = '';
            }

            return rawValue?.trim().toLowerCase() ?? '';
        };

        const sorted = [...filteredAssignments].sort((a, b) => {
            const valueA = getValue(a);
            const valueB = getValue(b);
            const comparison = valueA.localeCompare(valueB, undefined, { sensitivity: 'base' });

            return direction === SORT_DIRECTION.ASC ? comparison : -comparison;
        });

        return sorted;
    }, [filteredAssignments, sortConfig]);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(sortedAssignments.length / pageSize) - 1);

        if (currentPage > maxPage) {
            setCurrentPage(maxPage);
        }
    }, [currentPage, pageSize, setCurrentPage, sortedAssignments.length]);

    const paginatedAssignments = useMemo<RegionZoneDistrictMapping[]>(() => {
        if (sortedAssignments.length === 0) {
            return [];
        }

        const startIndex = currentPage * pageSize;
        return sortedAssignments.slice(startIndex, startIndex + pageSize);
    }, [currentPage, pageSize, sortedAssignments]);

    const pageStartCount = sortedAssignments.length === 0 ? 0 : currentPage * pageSize + 1;
    const pageEndCount =
        sortedAssignments.length === 0 ? 0 : Math.min(pageStartCount + pageSize - 1, sortedAssignments.length);
    const pageDisplayCount =
        sortedAssignments.length === 0
            ? '0-0 of 0'
            : `${pageStartCount}-${pageEndCount} of ${sortedAssignments.length}`;

    const handleSortChange = useCallback(
        (key: SortKey, direction: SORT_DIRECTION) => {
            setSortConfig({ key, direction });
            setCurrentPage(0);
        },
        [setCurrentPage, setSortConfig]
    );

    const handlePageChange = useCallback(
        (pageIndex: number) => {
            setCurrentPage(pageIndex);
        },
        [setCurrentPage]
    );

    const handlePageSizeChange = useCallback(
        (size: number) => {
            setPageSize(size);
            setCurrentPage(0);
        },
        [setCurrentPage, setPageSize]
    );

    const userOptions = useMemo<SelectOption[]>(() => {
        const seen = new Map<string, string>();

        allAssignments
            .filter((item) => {
                const regionCodeOrId = (item.regionCode || item.regionId || '').trim();
                if (selectedRegionId && regionCodeOrId !== selectedRegionId) {
                    return false;
                }

                const zoneCodeOrId = (item.zoneCode || item.zoneId || '').trim();
                if (selectedZoneId && zoneCodeOrId !== selectedZoneId) {
                    return false;
                }

                const districtCodeOrId = (item.districtCode || item.districtId || '').trim();
                if (selectedDistrictId && districtCodeOrId !== selectedDistrictId) {
                    return false;
                }

                return Boolean(item.accountId);
            })
            .forEach((item) => {
                if (!item.accountId || seen.has(item.accountId)) {
                    return;
                }

                const label =
                    formatUserDisplayLabel({
                        accountId: item.accountId,
                        soaUsername: item.soaUsername,
                        firstName: item.firstName,
                        lastName: item.lastName,
                    }) || item.accountId;

                seen.set(item.accountId, label);
            });

        return Array.from(seen.entries())
            .sort((a, b) => a[1].localeCompare(b[1]))
            .map(([value, title]) => ({ value, title }));
    }, [allAssignments, selectedDistrictId, selectedRegionId, selectedZoneId]);

    useEffect(() => {
        if (!selectedUserAccountId) {
            return;
        }

        if (userOptions.some((option) => option.value === selectedUserAccountId)) {
            return;
        }

        setSelectedUserAccountId('');
        setCurrentPage(0);
        resetEditingState();
    }, [resetEditingState, selectedUserAccountId, userOptions]);

    const regionPlaceholder = t('administration:manageDsqms.placeholders.region', 'Select a region');
    const zonePlaceholder = t('administration:manageDsqms.placeholders.zone', 'Select a zone');
    const districtPlaceholder = t('administration:manageDsqms.placeholders.district', 'Select a district');
    const userPlaceholder = t('administration:manageDsqms.placeholders.userName', 'Select a user');

    const locationSummary = useMemo(() => {
        const parts = [selectedRegionLabel, selectedZoneLabel, selectedDistrictLabel, selectedUserLabel].filter(
            Boolean
        );

        if (parts.length === 0) {
            return t('administration:manageDsqms.labels.allLocations', 'All locations');
        }

        return parts.join(' • ');
    }, [selectedDistrictLabel, selectedRegionLabel, selectedUserLabel, selectedZoneLabel, t]);

    const unassignedLabel = t('administration:manageDsqms.labels.unassigned', 'Unassigned');
    const emptyValueLabel = '—';

    const unassignOption: AssignableUserOption = useMemo(
        () => ({
            accountId: UNASSIGN_OPTION_ID,
            label: t('administration:manageDsqms.labels.unassignOption', 'Unassigned'),
            soaUsername: null,
            firstName: null,
            lastName: null,
        }),
        [t]
    );

    const handleSave = useCallback(
        async (assignment: RegionZoneDistrictMapping, accountIdToSave: string) => {
            const isUnassigning = accountIdToSave === UNASSIGN_OPTION_ID;
            const effectiveNewAccountId = isUnassigning ? null : accountIdToSave;
            if (!accountIdToSave) {
                return;
            }
            if (!isUnassigning && effectiveNewAccountId === assignment.accountId) {
                return;
            }

            if (isUnassigning && !assignment.accountId) {
                return;
            }

            setSavingAssignment(true);
            setActionError(null);

            try {
                const response = await accountsService.updateDistrictAssignment({
                    existingAccountRoleId: assignment.accountRoleId,
                    newAccountId: effectiveNewAccountId,
                    districtId: assignment.districtId,
                });

                if (!response.success) {
                    if (response.aborted) {
                        return;
                    }

                    throw response.data;
                }

                publicationDispatch(
                    publicationsActions.setToastMessage({
                        toast: {
                            toastMessage: t('administration:manageDsqms.success.update', 'DSQM updated successfully.'),
                            iconColor: 'white',
                            iconName: 'checkmark',
                            backgroundColor: 'greenOne',
                        },
                    })
                );

                resetEditingState();
                setRefreshingData(true);
                await loadAssignments({ suppressLoading: true });
                setRefreshingData(false);
            } catch (error: unknown) {
                const apiError = error as { message?: string; globalErrors?: string[] };
                const globalErrors = apiError?.globalErrors;
                const message =
                    (Array.isArray(globalErrors) && globalErrors[0]) ||
                    apiError?.message ||
                    t(
                        'administration:manageDsqms.errors.updateFailure',
                        'Unable to update the DSQM assignment. Please try again.'
                    );
                setActionError(message);
                publicationDispatch(
                    publicationsActions.setToastMessage({
                        toast: {
                            toastMessage: message,
                            iconColor: 'white',
                            iconName: 'x-close',
                            backgroundColor: 'redOne',
                        },
                    })
                );
            } finally {
                setSavingAssignment(false);
            }
        },
        [loadAssignments, publicationDispatch, resetEditingState, t]
    );

    const handleEnterEditMode = useCallback(
        (assignment: RegionZoneDistrictMapping) => {
            const rowId = getRowId(assignment);
            setEditingRowId(rowId);

            const initialOption = buildAssignableOption({
                accountId: assignment.accountId,
                soaUsername: assignment.soaUsername,
                firstName: assignment.firstName,
                lastName: assignment.lastName,
            });

            setSelectedAccountId(assignment.accountId ?? '');
            setSelectedAssignableOption(initialOption);
            setAccountsSearchTerm('');

            const usersToSet = initialOption ? [initialOption, ...availableAssignableUsers] : availableAssignableUsers;
            setAssignableUsers(usersToSet);
            setActionError(null);

            window.requestAnimationFrame(() => {
                updateDropDirection();
            });
        },
        [buildAssignableOption, getRowId, updateDropDirection, availableAssignableUsers]
    );

    const assignableUsersWithUnassign = useMemo(() => {
        return [unassignOption, ...assignableUsers];
    }, [assignableUsers, unassignOption]);

    return (
        <>
            <AdminHubHeader
                breadcrumbs={[
                    { title: t('user:breadcrumbs.adminHub', 'Administration Hub'), to: '/administration' },
                    { title: t('user:breadcrumbs.dsqmsReassignment', 'Manage DSQM Assignment') },
                ]}
            />

            <div className="container-fluid">
                <div className={classes.pageContainer}>
                    <div className={classes.selectGroup}>
                        <div className={classes.selectWrapper}>
                            <Typography variant="label">
                                {t('administration:manageDsqms.labels.region', 'Region')}
                            </Typography>
                            <FilterTypeahead
                                id="manage-dsqms-region-filter"
                                options={regionOptions}
                                value={selectedRegionId}
                                placeholder={regionPlaceholder}
                                disabled={loadingAssignments}
                                onValueChange={(nextValue) => {
                                    if (!nextValue) {
                                        regionManuallyClearedRef.current = true;
                                    }
                                    setSelectedRegionId(nextValue);
                                    setCurrentPage(0);
                                    resetEditingState();
                                }}
                            />
                        </div>
                        <div className={classes.selectWrapper}>
                            <Typography variant="label">
                                {t('administration:manageDsqms.labels.zone', 'Zone')}
                            </Typography>
                            <FilterTypeahead
                                id="manage-dsqms-zone-filter"
                                options={zoneOptions}
                                value={selectedZoneId}
                                placeholder={zonePlaceholder}
                                disabled={loadingAssignments}
                                onValueChange={(nextValue) => {
                                    if (!nextValue) {
                                        zoneManuallyClearedRef.current = true;
                                    }
                                    setSelectedZoneId(nextValue);
                                    setCurrentPage(0);
                                    resetEditingState();
                                }}
                            />
                        </div>

                        <div className={classes.selectWrapper}>
                            <Typography variant="label">
                                {t('administration:manageDsqms.labels.district', 'District')}
                            </Typography>
                            <FilterTypeahead
                                id="manage-dsqms-district-filter"
                                options={districtOptions}
                                value={selectedDistrictId}
                                placeholder={districtPlaceholder}
                                disabled={loadingAssignments}
                                onValueChange={(nextValue) => {
                                    if (!nextValue) {
                                        districtManuallyClearedRef.current = true;
                                    }
                                    setSelectedDistrictId(nextValue);
                                    setCurrentPage(0);
                                    resetEditingState();
                                }}
                            />
                        </div>
                        <div className={classes.selectWrapper}>
                            <Typography variant="label">{t('SOA Username')}</Typography>
                            <FilterTypeahead
                                id="manage-dsqms-user-filter"
                                options={userOptions}
                                value={selectedUserAccountId}
                                placeholder={userPlaceholder}
                                disabled={loadingAssignments}
                                onValueChange={(nextValue) => {
                                    setSelectedUserAccountId(nextValue);
                                    setCurrentPage(0);
                                    resetEditingState();
                                }}
                            />
                        </div>
                    </div>

                    {loadError && (
                        <div className={classes.statusMessage}>
                            <Typography variant="default">{loadError}</Typography>
                        </div>
                    )}

                    <div className={classes.tableSection}>
                        <Typography variant="h4" style={{ marginBottom: 4 }}>
                            {t('administration:manageDsqms.reassignmentTitle', 'District Assignments')}
                        </Typography>
                        <Typography variant="default" color="textDarkSecondary">
                            {locationSummary}
                        </Typography>

                        {actionError && (
                            <div className={classes.statusMessage}>
                                <Typography variant="default">{actionError}</Typography>
                            </div>
                        )}

                        {loadingAssignments ? (
                            <div className={classes.tableLoader}>
                                <LoadingCircle size={32} />
                            </div>
                        ) : sortedAssignments.length > 0 ? (
                            <>
                                <Table useFixedLayout className={classes.borderedTable}>
                                    <TableHead>
                                        <TableRow>
                                            <TableHeader
                                                className={classes.tableHeaderSub}
                                                sortable
                                                sortDirection={
                                                    sortConfig?.key === 'soaUsername' ? sortConfig.direction : undefined
                                                }
                                                onSort={(_, direction) => handleSortChange('soaUsername', direction)}
                                            >
                                                <Typography variant="label">
                                                    {t('administration:manageDsqms.columns.username', 'SOA Username')}
                                                </Typography>
                                            </TableHeader>
                                            <TableHeader
                                                className={classes.tableHeaderSub}
                                                sortable
                                                sortDirection={
                                                    sortConfig?.key === 'firstName' ? sortConfig.direction : undefined
                                                }
                                                onSort={(_, direction) => handleSortChange('firstName', direction)}
                                            >
                                                <Typography variant="label">
                                                    {t('administration:manageDsqms.columns.firstName', 'First Name')}
                                                </Typography>
                                            </TableHeader>
                                            <TableHeader
                                                className={classes.tableHeaderSub}
                                                sortable
                                                sortDirection={
                                                    sortConfig?.key === 'lastName' ? sortConfig.direction : undefined
                                                }
                                                onSort={(_, direction) => handleSortChange('lastName', direction)}
                                            >
                                                <Typography variant="label">
                                                    {t('administration:manageDsqms.columns.lastName', 'Last Name')}
                                                </Typography>
                                            </TableHeader>
                                            <TableHeader rowSpan={2} className={classes.tableHeaderSpanTwo}>
                                                <Typography variant="label">
                                                    {t('administration:manageDsqms.columns.region', 'Region')}
                                                </Typography>
                                            </TableHeader>
                                            <TableHeader rowSpan={2} className={classes.tableHeaderSpanTwo}>
                                                <Typography variant="label">
                                                    {t('administration:manageDsqms.columns.zone', 'Zone')}
                                                </Typography>
                                            </TableHeader>
                                            <TableHeader rowSpan={2} className={classes.tableHeaderSpanTwo}>
                                                <Typography variant="label">
                                                    {t('administration:manageDsqms.columns.district', 'District')}
                                                </Typography>
                                            </TableHeader>
                                            <TableHeader rowSpan={2} className={classes.tableHeaderSpanTwo}>
                                                <Typography variant="label">
                                                    {t('administration:manageDsqms.columns.actions', 'Actions')}
                                                </Typography>
                                            </TableHeader>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {refreshingData
                                            ? Array.from({
                                                  length: Math.min(pageSize, paginatedAssignments.length || 5),
                                              }).map((_, index) => (
                                                  <TableRow key={`skeleton-${index}`} className={classes.skeletonRow}>
                                                      <TableCell
                                                          className={classes.tableBodyCell}
                                                          classNameOuter={classes.tableCellOuter}
                                                      >
                                                          <div
                                                              className={classes.skeletonCell}
                                                              style={{ width: '80%', margin: '0 auto' }}
                                                          />
                                                      </TableCell>
                                                      <TableCell
                                                          className={classes.tableBodyCell}
                                                          classNameOuter={classes.tableCellOuter}
                                                      >
                                                          <div
                                                              className={classes.skeletonCell}
                                                              style={{ width: '70%' }}
                                                          />
                                                      </TableCell>
                                                      <TableCell
                                                          className={classes.tableBodyCell}
                                                          classNameOuter={classes.tableCellOuter}
                                                      >
                                                          <div
                                                              className={classes.skeletonCell}
                                                              style={{ width: '70%' }}
                                                          />
                                                      </TableCell>
                                                      <TableCell
                                                          className={classes.tableBodyCell}
                                                          classNameOuter={classes.tableCellOuter}
                                                      >
                                                          <div
                                                              className={classes.skeletonCell}
                                                              style={{ width: '50%' }}
                                                          />
                                                      </TableCell>
                                                      <TableCell
                                                          className={classes.tableBodyCell}
                                                          classNameOuter={classes.tableCellOuter}
                                                      >
                                                          <div
                                                              className={classes.skeletonCell}
                                                              style={{ width: '50%' }}
                                                          />
                                                      </TableCell>
                                                      <TableCell
                                                          className={classes.tableBodyCell}
                                                          classNameOuter={classes.tableCellOuter}
                                                      >
                                                          <div
                                                              className={classes.skeletonCell}
                                                              style={{ width: '50%' }}
                                                          />
                                                      </TableCell>
                                                      <TableCell
                                                          className={classes.tableBodyCell}
                                                          classNameOuter={classes.tableCellOuter}
                                                      >
                                                          <div
                                                              className={classes.skeletonCell}
                                                              style={{ width: '60%', margin: '0 auto' }}
                                                          />
                                                      </TableCell>
                                                  </TableRow>
                                              ))
                                            : paginatedAssignments.map((assignment) => {
                                                  const rowId = getRowId(assignment);
                                                  const isEditing = editingRowId === rowId;
                                                  const displayUsername = assignment.soaUsername || unassignedLabel;
                                                  const displayFirstName = assignment.firstName || emptyValueLabel;
                                                  const displayLastName = assignment.lastName || emptyValueLabel;
                                                  const rowKey = rowId;

                                                  return (
                                                      <TableRow
                                                          key={rowKey}
                                                          className={classNames(classes.tableBodyRow, {
                                                              [classes.editRow]: isEditing,
                                                          })}
                                                          data-editing-row={isEditing ? rowId : undefined}
                                                      >
                                                          <TableCell
                                                              className={classNames(
                                                                  classes.tableBodyCell,
                                                                  classes.dsqmCell
                                                              )}
                                                              classNameOuter={classes.tableCellOuter}
                                                          >
                                                              {isEditing ? (
                                                                  <div
                                                                      className={classes.usernameSelectWrapper}
                                                                      style={usernameMenuStyle}
                                                                  >
                                                                      <Typeahead<AssignableUserOption>
                                                                          id={`dsqm-user-typeahead-${rowId}`}
                                                                          labelKey="label"
                                                                          options={assignableUsersWithUnassign}
                                                                          flip
                                                                          dropup={shouldUseDropup}
                                                                          selected={
                                                                              selectedAssignableOption
                                                                                  ? [selectedAssignableOption]
                                                                                  : []
                                                                          }
                                                                          onChange={(selections) => {
                                                                              const [selection] = selections;

                                                                              if (
                                                                                  !selection ||
                                                                                  typeof selection === 'string'
                                                                              ) {
                                                                                  setSelectedAccountId('');
                                                                                  setSelectedAssignableOption(null);
                                                                                  setAccountsSearchTerm('');
                                                                                  return;
                                                                              }

                                                                              setSelectedAccountId(selection.accountId);
                                                                              setSelectedAssignableOption(selection);
                                                                              setAccountsSearchTerm('');
                                                                              if (
                                                                                  selection.accountId !==
                                                                                  UNASSIGN_OPTION_ID
                                                                              ) {
                                                                                  setAssignableUsers((previous) => {
                                                                                      if (
                                                                                          previous.some(
                                                                                              (option) =>
                                                                                                  option.accountId ===
                                                                                                  selection.accountId
                                                                                          )
                                                                                      ) {
                                                                                          return previous;
                                                                                      }

                                                                                      return [selection, ...previous];
                                                                                  });
                                                                              }
                                                                              setActionError(null);
                                                                              updateDropDirection();
                                                                          }}
                                                                          onInputChange={(text) => {
                                                                              setAccountsSearchTerm((previous) => {
                                                                                  if (previous === text) {
                                                                                      return previous;
                                                                                  }

                                                                                  return text;
                                                                              });
                                                                              updateDropDirection();
                                                                          }}
                                                                          filterBy={() => true}
                                                                          isLoading={loadingAssignableUsers}
                                                                          paginate={false}
                                                                          positionFixed
                                                                          placeholder={t(
                                                                              'administration:manageDsqms.placeholders.user',
                                                                              'Select a DSQM user'
                                                                          )}
                                                                          disabled={savingAssignment}
                                                                      />
                                                                  </div>
                                                              ) : (
                                                                  <div
                                                                      style={{
                                                                          textAlign: 'center',
                                                                          width: '100%',
                                                                          cursor: 'pointer',
                                                                          padding: '8px 4px',
                                                                          borderRadius: '4px',
                                                                          transition: 'all 0.2s ease',
                                                                          border: '1px solid transparent',
                                                                          minHeight: '32px',
                                                                          display: 'flex',
                                                                          alignItems: 'center',
                                                                          justifyContent: 'center',
                                                                      }}
                                                                      onMouseEnter={(e) => {
                                                                          e.currentTarget.style.backgroundColor =
                                                                              '#f0f0f0';
                                                                          e.currentTarget.style.borderColor = '#ddd';
                                                                      }}
                                                                      onMouseLeave={(e) => {
                                                                          e.currentTarget.style.backgroundColor =
                                                                              'transparent';
                                                                          e.currentTarget.style.borderColor =
                                                                              'transparent';
                                                                      }}
                                                                      onClick={(e) => {
                                                                          e.preventDefault();
                                                                          e.stopPropagation();
                                                                          if (
                                                                              !loadingAssignments &&
                                                                              !savingAssignment
                                                                          ) {
                                                                              handleEnterEditMode(assignment);
                                                                          }
                                                                      }}
                                                                      title="Click to edit assignment"
                                                                  >
                                                                      <Typography
                                                                          variant="labelRegular"
                                                                          style={{
                                                                              textAlign: 'center',
                                                                              width: '100%',
                                                                              pointerEvents: 'none',
                                                                          }}
                                                                      >
                                                                          {displayUsername}
                                                                      </Typography>
                                                                  </div>
                                                              )}
                                                          </TableCell>
                                                          <TableCell
                                                              className={classes.tableBodyCell}
                                                              classNameOuter={classes.tableCellOuter}
                                                          >
                                                              <Typography>{displayFirstName}</Typography>
                                                          </TableCell>
                                                          <TableCell
                                                              className={classes.tableBodyCell}
                                                              classNameOuter={classes.tableCellOuter}
                                                          >
                                                              <Typography>{displayLastName}</Typography>
                                                          </TableCell>
                                                          <TableCell
                                                              className={classes.tableBodyCell}
                                                              classNameOuter={classes.tableCellOuter}
                                                          >
                                                              <Typography>
                                                                  {assignment.regionCode ||
                                                                      assignment.regionId ||
                                                                      emptyValueLabel}
                                                              </Typography>
                                                          </TableCell>
                                                          <TableCell
                                                              className={classes.tableBodyCell}
                                                              classNameOuter={classes.tableCellOuter}
                                                          >
                                                              <Typography>
                                                                  {assignment.zoneCode ||
                                                                      assignment.zoneId ||
                                                                      emptyValueLabel}
                                                              </Typography>
                                                          </TableCell>
                                                          <TableCell
                                                              className={classes.tableBodyCell}
                                                              classNameOuter={classes.tableCellOuter}
                                                          >
                                                              <Typography>
                                                                  {assignment.districtCode ||
                                                                      assignment.districtId ||
                                                                      emptyValueLabel}
                                                              </Typography>
                                                          </TableCell>
                                                          <TableCell
                                                              className={classes.tableBodyCell}
                                                              classNameOuter={classes.tableCellOuter}
                                                          >
                                                              {isEditing ? (
                                                                  <div className={classes.editActions}>
                                                                      <Button
                                                                          variant="primary"
                                                                          disabled={
                                                                              savingAssignment ||
                                                                              !selectedAccountId ||
                                                                              (selectedAccountId !==
                                                                                  UNASSIGN_OPTION_ID &&
                                                                                  selectedAccountId ===
                                                                                      (assignment.accountId ?? '')) ||
                                                                              (selectedAccountId ===
                                                                                  UNASSIGN_OPTION_ID &&
                                                                                  !assignment.accountId)
                                                                          }
                                                                          onPress={() =>
                                                                              handleSave(assignment, selectedAccountId)
                                                                          }
                                                                      >
                                                                          {t('common:actions.save', 'Save')}
                                                                      </Button>
                                                                      <Button
                                                                          variant="tertiary"
                                                                          disabled={savingAssignment}
                                                                          onPress={resetEditingState}
                                                                      >
                                                                          {t('common:actions.cancel', 'Cancel')}
                                                                      </Button>
                                                                  </div>
                                                              ) : (
                                                                  <Button
                                                                      variant="tertiary"
                                                                      disabled={loadingAssignments || savingAssignment}
                                                                      onPress={() => handleEnterEditMode(assignment)}
                                                                  >
                                                                      {t(
                                                                          'administration:manageDsqms.actions.edit',
                                                                          'Edit'
                                                                      )}
                                                                  </Button>
                                                              )}
                                                          </TableCell>
                                                      </TableRow>
                                                  );
                                              })}
                                    </TableBody>
                                </Table>
                                <TablePagination
                                    page={currentPage}
                                    size={pageSize}
                                    total={sortedAssignments.length}
                                    displayCount={pageDisplayCount}
                                    disabled={loadingAssignments || savingAssignment}
                                    onClick={handlePageChange}
                                    onSizeChange={handlePageSizeChange}
                                    pageSizeOptions={Array.from(PAGE_SIZE_OPTIONS)}
                                />
                            </>
                        ) : (
                            <Typography variant="default" color="textDarkSecondary" style={{ marginTop: 8 }}>
                                {t(
                                    'administration:manageDsqms.emptyState',
                                    'No DSQM assignments for this selection yet.'
                                )}
                            </Typography>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
